import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, RefreshCw } from "lucide-react";

const SUGGESTIONS = [
    "When does my next premium fall due?",
    "Which of my policies are missing a nominee?",
    "What's my net worth right now?",
    "Summarise the documents in my vault.",
];

export default function AiChat() {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, busy]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setBusy(true);
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: msg }]);
        try {
            const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: "Sorry — I couldn't reach the AI just now. Please try again." },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setSessionId(null);
        setMessages([]);
        setInput("");
    };

    return (
        <div data-testid="ai-chat-page" className="flex flex-col h-screen">
            <PageHeader
                eyebrow="Ask LifeVault"
                title="A private assistant — grounded in your own vault."
                subtitle="Conversations stay on your account. Ask about policies, due dates, nominees, balances, documents."
                testid="chat-header"
                actions={
                    <Button variant="outline" onClick={reset} disabled={busy} data-testid="chat-reset" className="rounded-full">
                        <RefreshCw className="mr-2 h-4 w-4" /> New chat
                    </Button>
                }
            />

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-8 space-y-4">
                {messages.length === 0 ? (
                    <div className="max-w-2xl mx-auto text-center mt-10">
                        <Sparkles className="mx-auto h-9 w-9 text-accent" strokeWidth={1.5} />
                        <h2 className="mt-4 font-display text-2xl font-bold">Ask anything about your life vault.</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            I can answer only from the data you've added. The richer your vault, the better the answers.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-2 justify-center">
                            {SUGGESTIONS.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`suggestion-${s.slice(0, 12)}`}
                                    onClick={() => send(s)}
                                    className="pill hover:bg-secondary text-left"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="max-w-3xl mx-auto space-y-4">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                data-testid={`msg-${i}`}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[80%] rounded-2xl px-5 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-card border border-border"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {busy ? (
                            <div className="flex justify-start">
                                <div className="bg-card border border-border rounded-2xl px-5 py-3 text-sm font-mono text-muted-foreground inline-flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" /> thinking…
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-border bg-card px-8 py-4"
            >
                <div className="max-w-3xl mx-auto flex items-center gap-3">
                    <Input
                        data-testid="chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={busy}
                        placeholder="Ask about your vault…"
                        className="flex-1"
                    />
                    <Button type="submit" disabled={busy || !input.trim()} data-testid="chat-send" className="rounded-full">
                        <Send className="h-4 w-4 mr-2" /> Send
                    </Button>
                </div>
            </form>
        </div>
    );
}
